// Axial hex math: flat-top hexagons
// Shared between main game and renderer

export const SQRT3 = Math.sqrt(3);

export const FLAT_TOP_DIRS = [
  { q:  1, r:  0 },
  { q:  0, r:  1 },
  { q: -1, r:  1 },
  { q: -1, r:  0 },
  { q:  0, r: -1 },
  { q:  1, r: -1 },
];

/** Axial hex coord → world pixel (flat-top) */
export function hexToPixel(q, r, size) {
  const x = size * (3 / 2) * q;
  const y = size * (SQRT3 / 2 * q + SQRT3 * r);
  return { x, y };
}

/** World pixel → fractional axial (flat-top) */
export function pixelToHex(px, py, size) {
  const q = (2 / 3 * px) / size;
  const r = (-1 / 3 * px + SQRT3 / 3 * py) / size;
  return hexRound(q, r);
}

/** Round fractional axial to nearest hex */
export function hexRound(qf, rf) {
  const sf = -qf - rf;
  let q = Math.round(qf);
  let r = Math.round(rf);
  let s = Math.round(sf);
  const dq = Math.abs(q - qf);
  const dr = Math.abs(r - rf);
  const ds = Math.abs(s - sf);
  if (dq > dr && dq > ds) q = -r - s;
  else if (dr > ds) r = -q - s;
  return { q, r };
}

export function hexKey(q, r) {
  return `${q},${r}`;
}

export function getNeighbor(q, r, side) {
  const d = FLAT_TOP_DIRS[side];
  return { q: q + d.q, r: r + d.r };
}

export function oppositeSide(side) {
  return (side + 3) % 6;
}

export function getEffectiveSide(sides, rotation, sideIndex) {
  return sides[(((sideIndex - rotation) % 6) + 6) % 6];
}

/**
 * Get the 6 corner points of a flat-top hex centered at (cx,cy) with given size.
 * Returns array of {x,y} starting at angle 0 (right).
 */
export function hexCorners(cx, cy, size) {
  const corners = [];
  for (let i = 0; i < 6; i++) {
    const angle = (Math.PI / 180) * (60 * i);
    corners.push({
      x: cx + size * Math.cos(angle),
      y: cy + size * Math.sin(angle),
    });
  }
  return corners;
}

/** Axial distance between two hex coords */
export function hexDistance(a, b) {
  return (Math.abs(a.q - b.q) + Math.abs(a.q + a.r - b.q - b.r) + Math.abs(a.r - b.r)) / 2;
}

/** All hex coords within radius R of center (inclusive) */
export function hexesWithinRadius(center, radius) {
  const results = [];
  for (let q = -radius; q <= radius; q++) {
    const rMin = Math.max(-radius, -q - radius);
    const rMax = Math.min(radius, -q + radius);
    for (let r = rMin; r <= rMax; r++) {
      results.push({ q: center.q + q, r: center.r + r });
    }
  }
  return results;
}

/**
 * Get hex coords visible in a viewport rect (world coords), with buffer.
 * Returns array of {q,r} coords.
 */
export function getVisibleHexes(worldMinX, worldMinY, worldMaxX, worldMaxY, size) {
  const hexes = [];
  // Estimate q range
  const qMin = Math.floor(worldMinX / (size * 1.5)) - 2;
  const qMax = Math.ceil(worldMaxX / (size * 1.5)) + 2;

  for (let q = qMin; q <= qMax; q++) {
    const rMin = Math.floor(
      (worldMinY / (size * SQRT3)) - q / 2
    ) - 2;
    const rMax = Math.ceil(
      (worldMaxY / (size * SQRT3)) - q / 2
    ) + 2;
    for (let r = rMin; r <= rMax; r++) {
      hexes.push({ q, r });
    }
  }
  return hexes;
}
